/* eslint-disable no-nested-ternary */
import React, { PureComponent } from 'react';
import {
  string,
  bool,
  shape,
  arrayOf,
  func,
  oneOfType,
  oneOf,
} from 'prop-types';
import styled, { keyframes } from 'styled-components';
import { mainText } from 'aegisv2/styles/colors';
import ReactSelect, {
  components as componentSelect,
} from 'react-select-crehana';
import ArrowLeft from 'crehana-icons/ArrowLeft';
import Close from 'crehana-icons/Close';
import { grayMain, primaryMain, grayLighter } from 'crehana-ui/styles/colors';

const CircleAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;
const ArrowWrapper = styled.div`
  transform: rotate(${props => (props.isOpen ? '' : '-')}90deg);
  transition: transform 0.3s;
`;
const Indicator = styled.div`
  width: 1px;
  background-color: ${props => (props.white ? '#FFFFFF4a' : '#0099FF4d')};
`;

const LdsDualRing = styled.div`
  display: inline-block;
  width: 22px;
  height: 22px;
  &:after {
    content: ' ';
    display: block;
    width: 18px;
    height: 18px;
    margin: 1px;
    border-radius: 50%;
    border: 2px solid ${props => (props.white ? '#FFFFFF4a' : '#09f')};
    border-color: ${props =>
      props.white
        ? '#FFFFFF4a transparent #FFFFFF4a transparent'
        : '#09f transparent #09f transparent'};
    animation: ${CircleAnimation} 1.2s linear infinite;
  }
`;

const ContainerStyles = (base, { selectProps: { fullWidth } }) => ({
  ...base,
  width: fullWidth ? '100%' : 'fit-content',
});

const ControlStyles = (
  base,
  {
    selectProps: {
      controlStyles,
      white,
      isMulti,
      showDecorator,
      menuIsOpen,
      selectWrapped,
      noLeftBorder,
      noRightBorder,
    },
    isDisabled,
  },
) => {
  const whiteStyle = white && {
    borderColor: 'initial',
    backgroundColor: '#FFFFFF26', // 15% opacity
  };
  const decorator = showDecorator &&
    menuIsOpen && {
      '&:after': {
        content: '" "',
        backgroundColor: `${white ? '#262626' : '#fff'}`,
        position: 'absolute',
        margin: 'auto',
        bottom: '-20px',
        left: 0,
        right: 0,
        width: '15px',
        height: '15px',
        transform: 'rotate(225deg)',
        borderRight: `1px solid ${
          white ? 'transparent' : 'rgba(0, 153, 255, 0.3)'
        }`,
        borderBottom: `1px solid ${
          white ? 'transparent' : 'rgba(0, 153, 255, 0.3)'
        }`,
        zIndex: '10',
      },
    };

  return {
    ...base,
    boxShadow: 'none',
    backgroundColor: '#fff',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    fontFamily: 'Nunito, Arial, sans-serif',
    fontWeight: 300,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.010em',
    borderStyle: 'solid',
    borderWidth: selectWrapped && menuIsOpen ? '1px 1px 1px 1px' : '1px',
    borderColor: grayMain,
    borderRadius: 0,
    // borderRadius: selectWrapped && menuIsOpen ? '5px 5px 0 0' : '5px',
    padding: isMulti ? '15px' : '8px 15px',
    position: 'relative',
    transition: 'all 100ms',
    opacity: isDisabled ? 0.7 : 1,
    borderLeftWidth: noLeftBorder ? (!menuIsOpen ? '0' : '1px') : '1px',
    borderRightWidth: noRightBorder ? (!menuIsOpen ? '0' : '1px') : '1px',
    minHeight: '56px',
    '&:hover': {
      borderWidth: selectWrapped && menuIsOpen && '1px 1px 0 1px',
    },
    ...decorator,
    ...whiteStyle,
    ...controlStyles,
  };
};

const MenuStyles = (
  base,
  {
    selectProps: {
      menuPosition,
      white,
      showDecorator,
      menuStyles,
      selectWrapped,
    },
  },
) => {
  const whiteStyle = white && {
    borderColor: 'transparent',
    backgroundColor: '#FFFFFF26', // 15% opacity
  };

  return {
    ...base,
    cursor: 'pointer',
    marginTop: selectWrapped ? 0 : showDecorator ? '11px' : '8px',
    boxShadow: 'none',
    marginBottom: 0,
    fontFamily: 'Nunito, Arial, sans-serif',
    fontWeight: 300,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.010em',
    position: menuPosition || 'absolute',
    borderStyle: 'solid',
    borderWidth: selectWrapped ? '0 1px 1px 1px' : '1px',
    // borderRadius: selectWrapped ? '0 0 5px 5px' : '5px',
    boxShadow: '0px 6px 18px rgba(92, 84, 122, 0.1)',
    borderColor: '#fff',
    zIndex: '9',
    ...whiteStyle,
    ...menuStyles,
  };
};

const MenuListStyles = base => ({
  ...base,
  padding: 0,
});

const ValueContainerStyles = base => ({
  ...base,
  padding: 0,
});

const OptionStyles = (
  base,
  { isSelected, selectProps: { white, showDecorator, optionStyles } },
) => {
  const whiteStyle = white && {
    color: !isSelected ? '#fff' : primaryMain,
    '&:hover': {
      backgroundColor: '#FFFFFF10',
      color: !isSelected ? '#fff' : primaryMain,
    },
  };
  const decorator = showDecorator && {
    '&:hover': {
      backgroundColor: '#FFFFFF00',
      color: primaryMain,
    },
  };

  return {
    ...base,
    display: 'flex',
    alignItems: 'center',
    color: !isSelected ? mainText : primaryMain,
    backgroundColor: '#fff',
    height: '56px',
    '&:hover': {
      backgroundColor: grayLighter,
      color: !isSelected ? mainText : primaryMain,
      cursor: 'pointer',
    },
    ...decorator,
    ...whiteStyle,
    ...optionStyles,
  };
};

const LoadingIndicatorStyles = () => ({
  display: 'inline-block',
  width: '22px',
  height: '22px',
  marginLeft: 15,
});
const DropdownIndicatorStyles = () => ({
  display: 'flex',
  marginLeft: 15,
});
const IndicatorSeparatorStyles = () => ({
  display: 'flex',
  margin: '-10px 0 -10px 15px',
  width: '1px',
  alignSelf: 'stretch',
});
const ClearIndicatorStyles = () => ({
  padding: 0,
  display: 'flex',
  marginLeft: 15,
});

class Select extends PureComponent {
  // Custom Components
  DropdownIndicator = ({
    getStyles,
    selectProps: { renderArrow, menuIsOpen, white },
    ...props
  }) =>
    componentSelect.DropdownIndicator &&
    renderArrow && (
      <div style={getStyles('dropdownIndicator', props)}>
        <ArrowWrapper isOpen={menuIsOpen}>
          <ArrowLeft size={12} color={white ? '#cfcfcf' : primaryMain} />
        </ArrowWrapper>
      </div>
    );

  IndicatorSeparator = ({
    getStyles,
    selectProps: { renderIndicator, white },
  }) =>
    renderIndicator && (
      <div style={getStyles('indicatorSeparator', this.props)}>
        <Indicator white={white} />
      </div>
    );

  LoadingIndicator = ({ getStyles, selectProps: { isLoading, white } }) =>
    isLoading && (
      <div style={getStyles('loadingIndicator', this.props)}>
        <LdsDualRing white={white} />
      </div>
    );

  ClearIndicator = ({
    getStyles,
    innerProps: { ref, ...restInnerProps },
    selectProps: { isClearable, white },
    ...props
  }) =>
    isClearable && (
      <div
        {...restInnerProps}
        ref={ref}
        style={getStyles('clearIndicator', props)}
      >
        <Close
          size={10}
          color={white ? '#cfcfcf' : mainText}
          style={getStyles('clearIndicator', this.props)}
        />
      </div>
    );

  // Custom Styles
  PlaceHolderStyles = () => {
    const { white } = this.props;

    return {
      display: 'flex',
      color: white ? '#fff' : '#8b8c99',
    };
  };

  SingleValueStyles = () => {
    const { white } = this.props;

    return {
      color: white ? '#fff' : mainText,
    };
  };

  MultiValueStyles = base => {
    const { white } = this.props;

    return {
      ...base,
      // borderRadius: '5px',
      margin: '0 6px 0 0',
      backgroundColor: white ? '#cfcfcf' : '#0099FF40',
      color: white ? '#fff' : mainText,
      '&>div': {
        paddingTop: 0,
        paddingBottom: 0,
        color: white ? '#fff' : mainText,
      },
    };
  };

  MultiValueRemove = base => {
    const { white } = this.props;

    return {
      ...base,
      // borderRadius: '0 5px 5px 0',
      '&:hover': {
        backgroundColor: white ? '#FFFFFF26' : '#0099FF75',
        color: '#fff',
      },
    };
  };

  render() {
    const {
      components,
      white,
      isSearchable,
      placeholder,
      renderArrow,
      renderIndicator,
      isClearable,
      controlStyles,
      menuStyles,
      optionStyles,
      fullWidth,
      isLoading,
      selectWrapped,
      showDecorator,
      noLeftBorder,
      noRightBorder,
      styles,
      ...props
    } = this.props;

    return (
      <ReactSelect
        placeholder={placeholder}
        isSearchable={isSearchable}
        renderArrow={renderArrow}
        renderIndicator={renderIndicator}
        isClearable={isClearable}
        controlStyles={controlStyles}
        menuStyles={menuStyles}
        optionStyles={optionStyles}
        showDecorator={showDecorator}
        noLeftBorder={noLeftBorder}
        noRightBorder={noRightBorder}
        components={{
          DropdownIndicator: this.DropdownIndicator,
          IndicatorSeparator: this.IndicatorSeparator,
          LoadingIndicator: this.LoadingIndicator,
          ClearIndicator: this.ClearIndicator,
          ...components,
        }}
        fullWidth={fullWidth}
        isLoading={isLoading}
        white={white}
        selectWrapped={selectWrapped}
        styles={{
          container: ContainerStyles,
          placeholder: this.PlaceHolderStyles,
          dropdownIndicator: DropdownIndicatorStyles,
          loadingIndicator: LoadingIndicatorStyles,
          control: ControlStyles,
          menu: MenuStyles,
          menuList: MenuListStyles,
          valueContainer: ValueContainerStyles,
          singleValue: this.SingleValueStyles,
          option: OptionStyles,
          indicatorSeparator: IndicatorSeparatorStyles,
          clearIndicator: ClearIndicatorStyles,
          multiValue: this.MultiValueStyles,
          multiValueRemove: this.MultiValueRemove,
          ...styles,
        }}
        {...props}
      />
    );
  }
}

Select.propTypes = {
  controlStyles: shape({}),
  components: shape({}),
  fullWidth: bool,
  isClearable: bool,
  isLoading: bool,
  isSearchable: bool,
  isMulti: bool,
  menuStyles: shape({}),
  menuPosition: oneOf(['absolute', 'static', 'relative', 'fixed']),
  onChange: func,
  optionStyles: shape({}),
  options: arrayOf(shape({})),
  placeholder: string,
  renderArrow: bool,
  renderIndicator: bool,
  styles: shape({}),
  showDecorator: bool,
  selectWrapped: bool,
  value: oneOfType([shape({}), arrayOf(shape({}))]),
  white: bool,
  noLeftBorder: bool,
  noRightBorder: bool,
};

Select.defaultProps = {
  controlStyles: {},
  components: {},
  fullWidth: false,
  isClearable: false,
  isLoading: false,
  isSearchable: false,
  isMulti: false,
  menuPosition: 'absolute',
  menuStyles: {},
  onChange: undefined,
  optionStyles: {},
  options: [],
  placeholder: 'Select',
  renderArrow: false,
  renderIndicator: false,
  styles: {},
  showDecorator: false,
  selectWrapped: false,
  value: undefined,
  white: false,
  noLeftBorder: false,
  noRightBorder: false,
};

export default Select;
